import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

// Mirrors backend/src/modules/bank/bank.service.ts.

export type PlaidEnvironment = 'sandbox' | 'production';
export type BankAccountRole = 'cash' | 'owed' | 'ignore';

export interface BankAccount {
  id: string;
  name: string;
  officialName: string | null;
  mask: string | null;
  type: string;
  subtype: string | null;
  role: BankAccountRole;
  currentBalance: number | null;
  availableBalance: number | null;
  currency: string | null;
  balanceAt: string | null;
}

export interface BankConnection {
  id: string;
  institutionName: string | null;
  /** ok · login_required · error */
  status: string;
  lastError: string | null;
  lastSyncedAt: string | null;
  historyStatus: string | null;
  connectedBy: string | null;
  connectedAt: string;
  accounts: BankAccount[];
}

export interface CredentialEvent {
  id: string;
  action: string;
  summary: string;
  detail: string | null;
  ok: boolean;
  actorName: string | null;
  actorEmail: string | null;
  at: string;
}

export interface PlaidState {
  provider: 'PLAID';
  stored: boolean;
  usable: boolean;
  unreadable: boolean;
  reason: string | null;
  environment: PlaidEnvironment | null;
  clientIdMasked: string | null;
  connectedAt: string | null;
  connectedBy: string | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestReason: string | null;
  reviewFrom: string | null;
  canManage: boolean;
  history: CredentialEvent[];
  connections: BankConnection[];
  totals: { cash: number; owed: number; cashAccounts: number; owedAccounts: number; missing: number };
  snapshot: { date: string; cash: number; liabilities: number; source: string } | null;
  books: { asOf: string | null; cash: number | null; liabilities: number | null } | null;
}

export type MatchConfidence = 'strong' | 'likely' | 'possible';

export interface DepositInvoice {
  id: string;
  number: string;
  clientName: string;
  dueAmount: number;
  issueDate: string;
  dueDate: string;
}

export interface DepositSuggestion {
  kind: 'invoices' | 'already-recorded' | 'stripe-payout' | 'transfer';
  confidence: MatchConfidence;
  reason: string;
  invoiceIds?: string[];
  paymentIds?: string[];
  invoices?: DepositInvoice[];
  payments?: Array<{ id: string; invoiceNumber: string; clientName: string; amount: number; date: string }>;
}

export interface Deposit {
  id: string;
  date: string;
  amount: number;
  description: string;
  merchantName: string | null;
  counterparty: string | null;
  account: { name: string; mask: string | null; institution: string | null };
  status: 'review' | 'pending' | 'recorded' | 'dismissed';
  dismissed: { reason: string; note: string | null; by: string | null; at: string } | null;
  payments: Array<{ id: string; invoiceId: string; invoiceNumber: string; clientName: string; amount: number }>;
  suggestions: DepositSuggestion[];
}

export type DepositView = 'review' | 'recorded' | 'dismissed' | 'all';

export interface DepositsPayload {
  connected: boolean;
  reviewFrom: string | null;
  view: DepositView;
  counts: Record<'review' | 'pending' | 'recorded' | 'dismissed', number>;
  deposits: Deposit[];
}

export type DismissReason = 'stripe-payout' | 'transfer' | 'already-recorded' | 'other';

const PLAID_KEY = ['bank', 'plaid'] as const;

export function usePlaid() {
  return useQuery({
    queryKey: PLAID_KEY,
    queryFn: () => api.get<PlaidState>('/settings/integrations/plaid'),
  });
}

/** Every bank write can move Cash on hand and the deposit queue, so all of it refreshes. */
function useBankInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['bank'] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['cashflow'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

function usePlaidWrite<TInput, TOut = PlaidState>(fn: (input: TInput) => Promise<TOut>) {
  const invalidate = useBankInvalidate();
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
}

export const useConnectPlaid = () =>
  usePlaidWrite((input: { clientId: string; secret: string; environment: PlaidEnvironment }) =>
    api.post<PlaidState>('/settings/integrations/plaid', input),
  );

export const useTestPlaid = () =>
  usePlaidWrite(() =>
    api.post<{ ok: boolean; checkedAt: string; detail: string | null }>('/settings/integrations/plaid/test', {}),
  );

export const useRemovePlaidKeys = () =>
  usePlaidWrite(() => api.delete<{ removed: boolean }>('/settings/integrations/plaid'));

export const useSetReviewFrom = () =>
  usePlaidWrite((reviewFrom: string) => api.patch<PlaidState>('/settings/integrations/plaid', { reviewFrom }));

export const useLinkToken = () =>
  useMutation({
    mutationFn: (connectionId?: string) =>
      api.post<{ linkToken: string; expiration: string | null }>(
        '/settings/integrations/plaid/link-token',
        connectionId ? { connectionId } : {},
      ),
  });

export const useAddBank = () =>
  usePlaidWrite((input: { publicToken: string; institution: { id: string | null; name: string | null } | null }) =>
    api.post<PlaidState>('/settings/integrations/plaid/connections', input),
  );

export const useAddSandboxBank = () =>
  usePlaidWrite(() => api.post<PlaidState>('/settings/integrations/plaid/connections/sandbox', {}));

export const useBankReconnected = () =>
  usePlaidWrite((id: string) => api.post<PlaidState>(`/settings/integrations/plaid/connections/${id}/reconnected`, {}));

export const useRemoveBank = () =>
  usePlaidWrite((id: string) => api.delete<PlaidState>(`/settings/integrations/plaid/connections/${id}`));

export const useSetAccountRole = () =>
  usePlaidWrite((input: { id: string; role: BankAccountRole }) =>
    api.patch<PlaidState>(`/settings/integrations/plaid/accounts/${input.id}`, { role: input.role }),
  );

export const useSyncBank = () =>
  usePlaidWrite(() =>
    api.post<{
      connections: Array<{ institution: string | null; ok: boolean; added: number; error: string | null }>;
      snapshot: { written: boolean; message: string };
    }>('/settings/integrations/plaid/sync', {}),
  );

export function useDeposits(view: DepositView) {
  return useQuery({
    queryKey: ['bank', 'deposits', view],
    queryFn: () => api.get<DepositsPayload>('/bank/deposits', { params: { view } }),
    placeholderData: keepPreviousData,
  });
}

/** The /invoices chip. Quiet when no bank is connected. */
export function useDepositSummary() {
  return useQuery({
    queryKey: ['bank', 'deposits', 'summary'],
    queryFn: () => api.get<{ connected: boolean; toReview: number; amount: number }>('/bank/deposits/summary'),
    staleTime: 60_000,
  });
}

export const useRecordDeposit = () =>
  usePlaidWrite(
    (input: { id: string; invoices: Array<{ id: string; expectedDue: number }>; sendReceipt: boolean }) =>
      api.post<{
        recorded: number;
        results: Array<{
          invoiceId: string;
          number: string;
          amount: number;
          ok: boolean;
          error: string | null;
          receipt: { status: string; to: string | null } | null;
        }>;
      }>(`/bank/deposits/${input.id}/record`, { invoices: input.invoices, sendReceipt: input.sendReceipt }),
  );

export const useDismissDeposit = () =>
  usePlaidWrite((input: { id: string; reason: DismissReason; note?: string }) =>
    api.post<{ ok: boolean }>(`/bank/deposits/${input.id}/dismiss`, { reason: input.reason, note: input.note }),
  );

export const useRestoreDeposit = () =>
  usePlaidWrite((id: string) => api.post<{ ok: boolean }>(`/bank/deposits/${id}/restore`, {}));
